/**
 * features/reports/CompositeMarksheet.tsx
 * ------------------------------------------------------------------
 * Student Analysis (formerly "Academic Analysis").
 *
 * Tabs:
 *   1. Marksheet  — class or form-level student × subject grid
 *   2. At-Risk    — flagged students based on mark thresholds
 *   3. Summary    — class/form pass rates, subject comparison
 *   4. By Subject — department-scoped student performance (HOD-focused):
 *                   how students in the class/form are doing in a
 *                   department's subjects. Scoped to the HOD's
 *                   departments; admin/headteacher pick any.
 *
 * CSV + branded PDF export (shared autotable chrome) for the active view.
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  Table,
  Layers,
  Download,
  AlertTriangle,
  BarChart3,
  TrendingDown,
  BookOpen,
  FileText,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  deriveMark,
  gradeFor,
  getFormLevel,
  getPassMark,
  schemeForSubject,
  termUsesExam,
} from '@/lib/grading';
import { classifyAtRisk, type AtRiskStudent } from '@/lib/atRisk';
import { curriculumDepartmentsFor, getAccessibleClasses } from '@/lib/permissions';
import {
  allDepartments as allDepts,
  departmentOfSubject,
  isDeptVisible,
  sameDept,
} from '@/lib/departments';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildMarksheetPdf, buildAtRiskPdf, buildSummaryPdf, type MarksheetPdfRow } from '@/lib/analysisPdf';
import { termLabel } from '@/lib/school.config';
import { isJuneCohort } from '@/lib/cohort';
import { sortClasses } from '@/lib/classSort';
import {
  GradientHero,
  SoftCard,
  AccentButton,
  glassFieldClass,
} from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import type {
  MarksDocument,
  SubjectMarkEntry,
  Subject,
} from '@/types';

type AnalysisTab = 'marksheet' | 'atrisk' | 'summary' | 'bysubject';
type ViewMode = 'class' | 'form';

interface StudentRow {
  studentId: string;
  studentName: string;
  className: string;
  marks: Record<string, { termMark: number | null; grade: string }>;
  average: number | null;
  rank: number;
  subjectCount: number;
  passCount: number;
  failCount: number;
}

/** Median of a numeric list (rounded to 1dp); null for an empty list. The
 *  marksheet reports median alongside the mean so a few extreme marks don't
 *  distort the "typical" picture (§12). */
function median(nums: number[]): number | null {
  if (nums.length === 0) return null;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  const m = s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  return Math.round(m * 10) / 10;
}

/** Compact column label for a subject: its explicit shortName if set (never
 *  truncated), otherwise the full name clipped only when very long (§12 —
 *  replaces the old hard slice(0,6) that cut names mid-word). */
function subjectShort(s: Subject): string {
  const sn = s.shortName?.trim();
  if (sn) return sn;
  const n = s.name.trim();
  return n.length > 10 ? n.slice(0, 9) + '…' : n;
}

/**
 * Human-readable "why at-risk" summary for one student. Extracted so the
 * on-screen At-Risk cell AND the CSV/PDF exports emit IDENTICAL text.
 */
function describeAtRiskReason(s: AtRiskStudent, passMark: number): string {
  return [
    s.failedSubjects.length > 0 ? `${s.failedSubjects.length} failed subject${s.failedSubjects.length > 1 ? 's' : ''}` : '',
    s.englishFailed ? 'English failed' : '',
    s.borderlineSubjects.length >= 2 ? `${s.borderlineSubjects.length} borderline` : '',
    s.average !== null && s.average < passMark + 5 ? 'Low average' : '',
  ].filter(Boolean).join('; ') || '—';
}

export default function CompositeMarksheet({
  year,
  term,
}: {
  year: string;
  term: string;
}) {
  const { activeStudents: students, subjects, classes, teachers } = useData();
  const { userData } = useAuth();

  const [analysisTab, setAnalysisTab] = useState<AnalysisTab>('marksheet');
  const [viewMode, setViewMode] = useState<ViewMode>('class');
  const [sortMode, setSortMode] = useState<'rank' | 'name'>('rank');
  // Which mark drives the analysis: CA or the exam. Defaults to the exam in
  // exam terms (Term 2/3) so the analysis reflects the current exam; CA in
  // Term 1. June-cohort rows are always CA (they have no exam).
  const [basis, setBasis] = useState<'ca' | 'exam'>(termUsesExam(term) ? 'exam' : 'ca');
  useEffect(() => { setBasis(termUsesExam(term) ? 'exam' : 'ca'); }, [term]);
  const [classId, setClassId] = useState('');
  const [formLevel, setFormLevel] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [markDocs, setMarkDocs] = useState<MarksDocument[]>([]);
  const [loading, setLoading] = useState(false);

  // Year/class scope for the viewer — mirrors the four screens that already
  // thread this (TeacherDepartmentAnalysis, MarkEntry, ProgressDashboard):
  // admin/head/editor get all classes (early returns in getAccessibleClasses);
  // a HOY gets only their hoyForms' classes; class/subject teachers only theirs.
  const myClasses = useMemo(
    () => getAccessibleClasses(userData, classes, teachers, students),
    [userData, classes, teachers, students],
  );

  const sortedClasses = useMemo(() => sortClasses(myClasses), [myClasses]);

  const formLevels = useMemo(() => {
    const set = new Set<string>();
    myClasses.forEach((c) => { const f = getFormLevel(c.name); if (f) set.add(f); });
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [myClasses]);

  const targetClassIds = useMemo(() => {
    if (viewMode === 'class') return classId ? [classId] : [];
    if (viewMode === 'form' && formLevel) {
      return myClasses.filter((c) => getFormLevel(c.name) === formLevel).map((c) => c.id);
    }
    return [];
  }, [viewMode, classId, formLevel, myClasses]);

  useEffect(() => {
    if (targetClassIds.length === 0) { setMarkDocs([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const allDocs: MarksDocument[] = [];
        for (let i = 0; i < targetClassIds.length; i += 30) {
          const chunk = targetClassIds.slice(i, i + 30);
          const snap = await getDocs(
            query(collection(db, COLLECTIONS.marks), where('year', '==', year), where('term', '==', term), where('classId', 'in', chunk)),
          );
          snap.docs.forEach((d) => allDocs.push(d.data() as MarksDocument));
        }
        if (!cancelled) setMarkDocs(allDocs);
      } catch (e) { console.error('Failed to load marks:', e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [targetClassIds, year, term]);

  const relevantSubjects = useMemo(() => {
    const sids = new Set<string>();
    students.filter((s) => targetClassIds.includes(s.currentClass)).forEach((s) => s.subjects?.forEach((sid) => sids.add(sid)));
    return subjects.filter((s) => sids.has(s.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [students, subjects, targetClassIds]);

  // --- department scoping (By Subject tab, HOD-focused) -------------
  // Department is resolved canonically (lib/departments): explicit
  // Subject.department → teacher majority → Unassigned, matching the
  // Teacher.department / hodDepartments vocabulary. HODs are scoped to
  // their hodDepartments; admin/headteacher see all.
  const allDepartments = useMemo(
    () => allDepts(subjects, teachers),
    [subjects, teachers],
  );

  const myDepartments = useMemo(
    () => curriculumDepartmentsFor(userData, teachers),
    [userData, teachers],
  );

  const availableDepartments = useMemo(() => {
    if (myDepartments === null) return allDepartments; // admin / head
    const scoped = allDepartments.filter((d) => isDeptVisible(d, myDepartments));
    return scoped.length ? scoped : myDepartments;
  }, [myDepartments, allDepartments]);

  // Default / reconcile the department filter to something available.
  useEffect(() => {
    if (availableDepartments.length === 0) return;
    if (!departmentFilter || !availableDepartments.some((d) => sameDept(d, departmentFilter))) {
      setDepartmentFilter(availableDepartments[0]);
    }
  }, [availableDepartments, departmentFilter]);

  const departmentSubjects = useMemo(
    () =>
      relevantSubjects.filter((s) =>
        sameDept(departmentOfSubject(s, teachers), departmentFilter),
      ),
    [relevantSubjects, teachers, departmentFilter],
  );

  const targetStudents = useMemo(
    () => students.filter((s) => targetClassIds.includes(s.currentClass)).sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [students, targetClassIds],
  );

  const marksIndex = useMemo(() => {
    const idx: Record<string, Record<string, SubjectMarkEntry>> = {};
    for (const doc of markDocs) {
      for (const [sid, entry] of Object.entries(doc.entries)) {
        if (!idx[sid]) idx[sid] = {};
        idx[sid][doc.subjectId] = entry;
      }
    }
    return idx;
  }, [markDocs]);

  const currentFormLevel = useMemo(() => {
    if (viewMode === 'form') return formLevel;
    const cls = classes.find((c) => c.id === classId);
    return getFormLevel(cls?.name ?? '') ?? '';
  }, [viewMode, classId, formLevel, classes]);
  const passMark = getPassMark(currentFormLevel);

  const rows: StudentRow[] = useMemo(() => {
    const result: StudentRow[] = [];
    for (const s of targetStudents) {
      const cls = classes.find((c) => c.id === s.currentClass);
      const marks: StudentRow['marks'] = {};
      const termMarks: number[] = [];
      let passCount = 0, failCount = 0;
      for (const sub of relevantSubjects) {
        const entry = marksIndex[s.id]?.[sub.id];
        if (entry) {
          // June cohort grades CA-only (their Term 1), even though data sits
          // under the global term. January classes unchanged.
          const jc = isJuneCohort(cls);
          const scheme = schemeForSubject(sub);
          const d = deriveMark(jc ? 'Term1' : term, entry.ca, jc ? undefined : entry.exam, scheme);
          // Analyse the exam mark when the Exam basis is selected (never for
          // June — they have no exam); otherwise the CA mark. Grade matches.
          const useExam = basis === 'exam' && !jc;
          const exNum = (d.exam === '' || d.exam == null || d.exam === 'ABS') ? null : Number(d.exam);
          const value = useExam ? exNum : d.caMark;
          marks[sub.id] = { termMark: value, grade: gradeFor(value, scheme) };
          // Pass/fail + average follow the SELECTED basis (exam in exam terms),
          // matching the report and the grid — not the CA-only d.termMark.
          if (value !== null) {
            termMarks.push(value);
            if (value >= passMark) passCount++; else failCount++;
          }
        }
      }
      const avg = termMarks.length > 0 ? Math.round((termMarks.reduce((a, b) => a + b, 0) / termMarks.length) * 10) / 10 : null;
      result.push({ studentId: s.id, studentName: s.fullName, className: cls?.name ?? '', marks, average: avg, rank: 0, subjectCount: termMarks.length, passCount, failCount });
    }
    const withAvg = result.filter((r) => r.average !== null);
    withAvg.sort((a, b) => (b.average ?? 0) - (a.average ?? 0));
    let rank = 0, lastAvg: number | null = null;
    for (const r of withAvg) { if (r.average !== lastAvg) rank++; r.rank = rank; lastAvg = r.average; }
    result.sort((a, b) => { if (a.rank && b.rank) return a.rank - b.rank; if (a.rank) return -1; if (b.rank) return 1; return a.studentName.localeCompare(b.studentName); });
    return result;
  }, [targetStudents, relevantSubjects, marksIndex, term, passMark, classes, basis]);

  const subjectAverages = useMemo(() => {
    const avgs: Record<string, { total: number; count: number }> = {};
    for (const row of rows) {
      for (const [sid, m] of Object.entries(row.marks)) {
        if (m.termMark !== null) { if (!avgs[sid]) avgs[sid] = { total: 0, count: 0 }; avgs[sid].total += m.termMark; avgs[sid].count++; }
      }
    }
    const result: Record<string, number> = {};
    for (const [sid, v] of Object.entries(avgs)) result[sid] = Math.round((v.total / v.count) * 10) / 10;
    return result;
  }, [rows]);

  const classAverage = useMemo(() => {
    const avgs = rows.filter((r) => r.average !== null).map((r) => r.average!);
    return avgs.length > 0 ? Math.round((avgs.reduce((a, b) => a + b, 0) / avgs.length) * 10) / 10 : null;
  }, [rows]);

  // Subjects that actually have ≥1 captured mark — empty columns are hidden
  // from the grid and every export (§12 "exclude empty subjects").
  const shownSubjects = useMemo(() => {
    const ids = new Set<string>();
    for (const r of rows) for (const [sid, m] of Object.entries(r.marks)) if (m.termMark !== null) ids.add(sid);
    return relevantSubjects.filter((s) => ids.has(s.id));
  }, [rows, relevantSubjects]);

  // Per-subject and whole-class MEDIAN (typical mark), reported beside the mean.
  const subjectMedians = useMemo(() => {
    const out: Record<string, number> = {};
    for (const s of shownSubjects) {
      const m = median(rows.map((r) => r.marks[s.id]?.termMark).filter((v): v is number => v != null));
      if (m !== null) out[s.id] = m;
    }
    return out;
  }, [shownSubjects, rows]);
  const classMedian = useMemo(
    () => median(rows.map((r) => r.average).filter((v): v is number => v != null)),
    [rows],
  );

  // Display/export ordering: by rank (default) or alphabetically by name (§12).
  const displayRows = useMemo(
    () => (sortMode === 'name' ? [...rows].sort((a, b) => a.studentName.localeCompare(b.studentName)) : rows),
    [rows, sortMode],
  );

  // Department-scoped rows: same students, but average/pass/fail/rank
  // computed over ONLY the selected department's subjects.
  const deptRows: StudentRow[] = useMemo(() => {
    if (departmentSubjects.length === 0) return [];
    const result: StudentRow[] = targetStudents.map((s) => {
      const cls = classes.find((c) => c.id === s.currentClass);
      const marks: StudentRow['marks'] = {};
      const termMarks: number[] = [];
      let passCount = 0, failCount = 0;
      for (const sub of departmentSubjects) {
        const entry = marksIndex[s.id]?.[sub.id];
        if (entry) {
          // June cohort grades CA-only (their Term 1), even though data sits
          // under the global term. January classes unchanged.
          const jc = isJuneCohort(cls);
          const scheme = schemeForSubject(sub);
          const d = deriveMark(jc ? 'Term1' : term, entry.ca, jc ? undefined : entry.exam, scheme);
          // Analyse the exam mark when the Exam basis is selected (never for
          // June — they have no exam); otherwise the CA mark. Grade matches.
          const useExam = basis === 'exam' && !jc;
          const exNum = (d.exam === '' || d.exam == null || d.exam === 'ABS') ? null : Number(d.exam);
          const value = useExam ? exNum : d.caMark;
          marks[sub.id] = { termMark: value, grade: gradeFor(value, scheme) };
          // Pass/fail + average follow the SELECTED basis (exam in exam terms),
          // matching the report and the grid — not the CA-only d.termMark.
          if (value !== null) {
            termMarks.push(value);
            if (value >= passMark) passCount++; else failCount++;
          }
        }
      }
      const avg = termMarks.length > 0 ? Math.round((termMarks.reduce((a, b) => a + b, 0) / termMarks.length) * 10) / 10 : null;
      return { studentId: s.id, studentName: s.fullName, className: cls?.name ?? '', marks, average: avg, rank: 0, subjectCount: termMarks.length, passCount, failCount };
    });
    const withAvg = result.filter((r) => r.average !== null).sort((a, b) => (b.average ?? 0) - (a.average ?? 0));
    let rank = 0, lastAvg: number | null = null;
    for (const r of withAvg) { if (r.average !== lastAvg) rank++; r.rank = rank; lastAvg = r.average; }
    result.sort((a, b) => { if (a.rank && b.rank) return a.rank - b.rank; if (a.rank) return -1; if (b.rank) return 1; return a.studentName.localeCompare(b.studentName); });
    return result;
  }, [departmentSubjects, targetStudents, marksIndex, term, passMark, classes, basis]);

  const deptSubjectAverages = useMemo(() => {
    const avgs: Record<string, { total: number; count: number }> = {};
    for (const row of deptRows) {
      for (const sub of departmentSubjects) {
        const m = row.marks[sub.id];
        if (m && m.termMark !== null) { if (!avgs[sub.id]) avgs[sub.id] = { total: 0, count: 0 }; avgs[sub.id].total += m.termMark; avgs[sub.id].count++; }
      }
    }
    const result: Record<string, number> = {};
    for (const [sid, v] of Object.entries(avgs)) result[sid] = Math.round((v.total / v.count) * 10) / 10;
    return result;
  }, [deptRows, departmentSubjects]);

  const deptClassAverage = useMemo(() => {
    const avgs = deptRows.filter((r) => r.average !== null).map((r) => r.average!);
    return avgs.length > 0 ? Math.round((avgs.reduce((a, b) => a + b, 0) / avgs.length) * 10) / 10 : null;
  }, [deptRows]);

  const shownDeptSubjects = useMemo(() => {
    const ids = new Set<string>();
    for (const r of deptRows) for (const s of departmentSubjects) { const m = r.marks[s.id]; if (m && m.termMark != null) ids.add(s.id); }
    return departmentSubjects.filter((s) => ids.has(s.id));
  }, [deptRows, departmentSubjects]);
  const deptSubjectMedians = useMemo(() => {
    const out: Record<string, number> = {};
    for (const s of shownDeptSubjects) {
      const m = median(deptRows.map((r) => r.marks[s.id]?.termMark).filter((v): v is number => v != null));
      if (m !== null) out[s.id] = m;
    }
    return out;
  }, [shownDeptSubjects, deptRows]);
  const deptClassMedian = useMemo(
    () => median(deptRows.map((r) => r.average).filter((v): v is number => v != null)),
    [deptRows],
  );
  const displayDeptRows = useMemo(
    () => (sortMode === 'name' ? [...deptRows].sort((a, b) => a.studentName.localeCompare(b.studentName)) : deptRows),
    [deptRows, sortMode],
  );

  // At-risk computation
  const atRiskStudents = useMemo(() => {
    if (rows.length === 0 || targetStudents.length === 0) return [];
    // Build matrix: studentId -> subjectId -> numeric mark or null
    const matrix: Record<string, Record<string, number | null>> = {};
    for (const row of rows) {
      const studentMarks: Record<string, number | null> = {};
      for (const sub of relevantSubjects) {
        const m = row.marks[sub.id];
        studentMarks[sub.id] = m?.termMark ?? null;
      }
      matrix[row.studentId] = studentMarks;
    }
    return classifyAtRisk({
      students: targetStudents,
      subjects: relevantSubjects,
      passMark,
      matrix,
    });
  }, [rows, targetStudents, relevantSubjects, passMark]);

  const criticalCount = atRiskStudents.filter((s) => s.tier === 'critical').length;
  const borderlineCount = atRiskStudents.length - criticalCount;

  // Canonical Summary datasets — computed ONCE here so the Summary render and
  // the CSV/PDF exports read the same numbers (no screen/export drift).
  const summaryStats = useMemo(
    () =>
      shownSubjects.map((sub) => {
        const marks = rows.map((r) => r.marks[sub.id]?.termMark).filter((m): m is number => m !== null && m !== undefined);
        const avg = marks.length > 0 ? Math.round((marks.reduce((a, b) => a + b, 0) / marks.length) * 10) / 10 : null;
        const med = median(marks);
        const passRate = marks.length > 0 ? Math.round((marks.filter((m) => m >= passMark).length / marks.length) * 100) : null;
        const highest = marks.length > 0 ? Math.max(...marks) : null;
        const lowest = marks.length > 0 ? Math.min(...marks) : null;
        return { subject: sub, avg, median: med, passRate, highest, lowest, entryCount: marks.length };
      }).sort((a, b) => (b.avg ?? 0) - (a.avg ?? 0)),
    [shownSubjects, rows, passMark],
  );

  const avgDistribution = useMemo(() => {
    const buckets = [
      { label: '80–100', min: 80, max: 100, color: 'bg-emerald-500' },
      { label: '60–79', min: 60, max: 79, color: 'bg-emerald-300' },
      { label: '50–59', min: 50, max: 59, color: 'bg-amber-400' },
      { label: '40–49', min: 40, max: 49, color: 'bg-orange-400' },
      { label: '0–39', min: 0, max: 39, color: 'bg-red-400' },
    ];
    const avgs = rows.filter((r) => r.average !== null).map((r) => r.average!);
    return buckets.map((b) => ({ ...b, count: avgs.filter((a) => a >= b.min && a <= b.max).length }));
  }, [rows]);

  // Whether the ACTIVE tab has data to export (gates the export buttons).
  const hasExportData =
    analysisTab === 'atrisk' ? atRiskStudents.length > 0
      : analysisTab === 'summary' ? summaryStats.length > 0
        : analysisTab === 'bysubject' ? deptRows.length > 0
          : rows.length > 0;

  function markColor(mark: number | null): string {
    if (mark === null) return '';
    if (mark >= passMark + 25) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
    if (mark >= passMark) return 'bg-emerald-50/50 text-emerald-600 dark:bg-emerald-900/10 dark:text-emerald-400';
    if (mark >= passMark - 10) return 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
    return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300';
  }

  function downloadCsv(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportCsv() {
    const label = viewMode === 'class' ? classes.find((c) => c.id === classId)?.name ?? 'class' : `Form_${formLevel}`;
    const fileScope = `${label}_${term}_${year}`.replace(/\s+/g, '_');
    const csvEsc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;

    if (analysisTab === 'atrisk') {
      const lines = [['Student', 'Tier', 'Average', 'Failed Subjects', 'Reason'].map(csvEsc).join(',')];
      for (const s of atRiskStudents) {
        lines.push([
          csvEsc(s.fullName), csvEsc(s.tier), csvEsc(s.average ?? ''),
          csvEsc(s.failedSubjects.map((f) => f.subjectName).join('; ')),
          csvEsc(describeAtRiskReason(s, passMark)),
        ].join(','));
      }
      downloadCsv(lines.join('\n'), `AtRisk_${fileScope}.csv`);
      return;
    }

    if (analysisTab === 'summary') {
      const lines = [['Subject', 'Average', 'Median', 'Pass %', 'Highest', 'Lowest'].map(csvEsc).join(',')];
      for (const s of summaryStats) {
        lines.push([csvEsc(s.subject.name), csvEsc(s.avg ?? ''), csvEsc(s.median ?? ''), csvEsc(s.passRate ?? ''), csvEsc(s.highest ?? ''), csvEsc(s.lowest ?? '')].join(','));
      }
      lines.push('');
      lines.push(['Average band', 'Students'].map(csvEsc).join(','));
      for (const b of avgDistribution) lines.push([csvEsc(b.label), csvEsc(b.count)].join(','));
      downloadCsv(lines.join('\n'), `Summary_${fileScope}.csv`);
      return;
    }

    // marksheet + bysubject: the matrix CSV, from the right rows source per tab.
    const isDept = analysisTab === 'bysubject';
    const subs = isDept ? shownDeptSubjects : shownSubjects;
    const src = isDept ? displayDeptRows : displayRows;
    const subjAvg = isDept ? deptSubjectAverages : subjectAverages;
    const subjMed = isDept ? deptSubjectMedians : subjectMedians;
    const clsAvg = isDept ? deptClassAverage : classAverage;
    const clsMed = isDept ? deptClassMedian : classMedian;
    const headers = ['Rank', 'Student', ...(viewMode === 'form' ? ['Class'] : []), ...subs.map((s) => s.name), 'Average', 'Pass', 'Fail'];
    const csvRows = [headers.join(',')];
    for (const r of src) {
      const cells = [r.rank || '', `"${r.studentName}"`, ...(viewMode === 'form' ? [`"${r.className}"`] : []),
        ...subs.map((s) => r.marks[s.id]?.termMark ?? ''), r.average ?? '', r.passCount, r.failCount];
      csvRows.push(cells.join(','));
    }
    csvRows.push(['', '"AVERAGE"', ...(viewMode === 'form' ? [''] : []), ...subs.map((s) => subjAvg[s.id] ?? ''), clsAvg ?? '', '', ''].join(','));
    csvRows.push(['', '"MEDIAN"', ...(viewMode === 'form' ? [''] : []), ...subs.map((s) => subjMed[s.id] ?? ''), clsMed ?? '', '', ''].join(','));
    downloadCsv(csvRows.join('\n'), `${isDept ? 'StudentAnalysis_' + departmentFilter + '_' + fileScope : 'Marksheet_' + fileScope}.csv`.replace(/\s+/g, '_'));
  }

  async function exportPdf() {
    const scopeLabel = viewMode === 'class' ? (classes.find((c) => c.id === classId)?.name ?? 'class') : `Form ${formLevel}`;
    const fileScope = `${scopeLabel}_${term}_${year}`.replace(/\s+/g, '_');
    // June-cohort class scope shows their own Term 1 on the analysis header.
    const termText = (viewMode === 'class' && isJuneCohort(classes.find((c) => c.id === classId))) ? 'Term 1' : termLabel(term);

    if (analysisTab === 'atrisk') {
      if (atRiskStudents.length === 0) return;
      const logo = await loadLogoDataUrl();
      buildAtRiskPdf({
        title: 'STUDENT ANALYSIS - AT-RISK',
        metaLine: `${scopeLabel} - ${termText} ${year}`,
        rows: atRiskStudents.map((s) => ({
          name: s.fullName,
          tier: s.tier,
          average: s.average,
          failedSubjects: s.failedSubjects.map((f) => f.subjectName),
          reason: describeAtRiskReason(s, passMark),
        })),
        criticalCount,
        borderlineCount,
        logoDataUrl: logo,
        generatedBy: userData?.displayName,
      }).save(`AtRisk_${fileScope}.pdf`);
      return;
    }

    if (analysisTab === 'summary') {
      if (summaryStats.length === 0) return;
      const logo = await loadLogoDataUrl();
      buildSummaryPdf({
        title: 'STUDENT ANALYSIS - SUMMARY',
        metaLine: `${scopeLabel} - ${termText} ${year}`,
        subjects: summaryStats.map((s) => ({
          subject: s.subject.name,
          avg: s.avg,
          median: s.median,
          passRate: s.passRate,
          highest: s.highest,
          lowest: s.lowest,
        })),
        distribution: avgDistribution.map((b) => ({ label: b.label, count: b.count })),
        logoDataUrl: logo,
        generatedBy: userData?.displayName,
      }).save(`Summary_${fileScope}.pdf`);
      return;
    }

    // ----- marksheet + bysubject: matrix path (shown subjects + medians) -----
    const isDept = analysisTab === 'bysubject';
    const subs = isDept ? shownDeptSubjects : shownSubjects;
    const src = isDept ? displayDeptRows : displayRows;
    const subjAvg = isDept ? deptSubjectAverages : subjectAverages;
    const subjMed = isDept ? deptSubjectMedians : subjectMedians;
    const clsAvg = isDept ? deptClassAverage : classAverage;
    const clsMed = isDept ? deptClassMedian : classMedian;
    if (src.length === 0) return;
    const logo = await loadLogoDataUrl();
    const pdfRows: MarksheetPdfRow[] = src.map((r) => ({
      rank: r.rank,
      name: r.studentName,
      className: r.className,
      marks: Object.fromEntries(subs.map((s) => [s.id, r.marks[s.id]?.termMark ?? null])),
      average: r.average,
      pass: r.passCount,
      fail: r.failCount,
    }));
    const metaLine = [isDept ? departmentFilter : null, scopeLabel, `${termText} ${year}`, `${src.length} students`].filter(Boolean).join('  ·  ');
    buildMarksheetPdf({
      title: isDept ? 'STUDENT ANALYSIS · BY SUBJECT' : 'STUDENT ANALYSIS',
      metaLine,
      subjects: subs.map((s) => ({ id: s.id, name: s.name })),
      rows: pdfRows,
      subjectAverages: subjAvg,
      subjectMedians: subjMed,
      classAverage: clsAvg,
      classMedian: clsMed,
      passMark,
      showClassCol: viewMode === 'form',
      logoDataUrl: logo,
      generatedBy: userData?.displayName,
    }).save(
      `${isDept ? 'StudentAnalysis_' + departmentFilter : 'Marksheet'}_${scopeLabel}_${term}_${year}.pdf`.replace(/\s+/g, '_'),
    );
  }

  const analysisTabs = [
    { id: 'marksheet' as const, label: 'Marksheet', icon: Table },
    { id: 'atrisk' as const, label: `At-Risk${atRiskStudents.length > 0 ? ` (${atRiskStudents.length})` : ''}`, icon: AlertTriangle },
    { id: 'summary' as const, label: 'Summary', icon: BarChart3 },
    ...(availableDepartments.length > 0
      ? [{ id: 'bysubject' as const, label: 'By Subject', icon: BookOpen }]
      : []),
  ];

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Table}
        title="Student Analysis"
        subtitle={`${term.replace('Term', 'Term ')} ${year} · Marksheets, at-risk detection, summaries, and by-subject (department) analysis`}
      />

      {/* Filters */}
      <SoftCard pad="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-700">
            <button onClick={() => { setViewMode('class'); setFormLevel(''); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'class' ? 'seg-on' : 'text-gray-500'}`}>
              <Table size={15} /> Class
            </button>
            <button onClick={() => { setViewMode('form'); setClassId(''); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'form' ? 'seg-on' : 'text-gray-500'}`}>
              <Layers size={15} /> Form
            </button>
          </div>
          {viewMode === 'class' && (
            <select value={classId} onChange={(e) => setClassId(e.target.value)} className={glassFieldClass('sm:w-56')}>
              <option value="">— select class —</option>
              {sortedClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {viewMode === 'form' && (
            <select value={formLevel} onChange={(e) => setFormLevel(e.target.value)} className={glassFieldClass('sm:w-56')}>
              <option value="">— select form —</option>
              {formLevels.map((f) => <option key={f} value={f}>Form {f}</option>)}
            </select>
          )}
          {termUsesExam(term) && (
            <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-700" title="Analyse the CA mark or the exam mark">
              <button onClick={() => setBasis('ca')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${basis === 'ca' ? 'seg-on' : 'text-gray-500'}`}>
                CA
              </button>
              <button onClick={() => setBasis('exam')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${basis === 'exam' ? 'seg-on' : 'text-gray-500'}`}>
                Exam
              </button>
            </div>
          )}
          {(analysisTab === 'marksheet' || analysisTab === 'bysubject') && hasExportData && (
            <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-700 sm:ml-auto" title="Row order">
              <button onClick={() => setSortMode('rank')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${sortMode === 'rank' ? 'seg-on' : 'text-gray-500'}`}>
                Rank
              </button>
              <button onClick={() => setSortMode('name')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${sortMode === 'name' ? 'seg-on' : 'text-gray-500'}`}>
                A–Z
              </button>
            </div>
          )}
          {hasExportData && <AccentButton variant="soft" icon={Download} onClick={exportCsv}>Export CSV</AccentButton>}
          {hasExportData && <AccentButton variant="soft" icon={FileText} onClick={exportPdf}>Export PDF</AccentButton>}
        </div>
      </SoftCard>

      {loading && <Spinner label="Loading marks…" />}

      {!loading && targetClassIds.length === 0 && (
        <SoftCard><p className="text-sm text-gray-500 text-center py-8">Select a {viewMode === 'class' ? 'class' : 'form level'} to begin analysis.</p></SoftCard>
      )}

      {!loading && targetClassIds.length > 0 && (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <SoftCard pad="p-3"><p className="text-xl font-bold text-gray-800 dark:text-white">{rows.length}</p><p className="text-xs text-gray-400">Students</p></SoftCard>
            <SoftCard pad="p-3"><p className="text-xl font-bold text-gray-800 dark:text-white">{relevantSubjects.length}</p><p className="text-xs text-gray-400">Subjects</p></SoftCard>
            <SoftCard pad="p-3"><p className="text-xl font-bold text-emerald-600">{rows.filter((r) => r.average !== null && r.average >= passMark).length}</p><p className="text-xs text-gray-400">Passing</p></SoftCard>
            <SoftCard pad="p-3"><p className="text-xl font-bold text-red-600">{rows.filter((r) => r.average !== null && r.average < passMark).length}</p><p className="text-xs text-gray-400">Below Pass</p></SoftCard>
            <SoftCard pad="p-3"><p className="text-xl font-bold text-gray-800 dark:text-white">{classAverage ?? '—'}</p><p className="text-xs text-gray-400">Average</p></SoftCard>
          </div>

          {/* Analysis tabs */}
          <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
            {analysisTabs.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setAnalysisTab(t.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    analysisTab === t.id ? 'seg-on' : 'seg-off'
                  }`}>
                  <Icon size={15} /> <span className="hidden sm:inline">{t.label}</span>
                </button>
              );
            })}
          </div>

          {analysisTab === 'marksheet' && renderMarksheet()}
          {analysisTab === 'atrisk' && renderAtRisk()}
          {analysisTab === 'summary' && renderSummary()}
          {analysisTab === 'bysubject' && renderBySubject()}
        </>
      )}
    </div>
  );

  function renderMatrix(
    subjectList: Subject[],
    matrixRows: StudentRow[],
    subjAvg: Record<string, number>,
    clsAvg: number | null,
    subjMed: Record<string, number>,
    clsMed: number | null,
  ) {
    return (
      <SoftCard pad="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="sticky left-0 bg-gray-50 dark:bg-gray-800 z-10 px-3 py-2.5 text-left font-semibold text-gray-500 w-8" title="Row number = total students">No.</th>
                <th className="sticky left-8 bg-gray-50 dark:bg-gray-800 z-10 px-3 py-2.5 text-left font-semibold text-gray-500 min-w-[150px]">Student</th>
                {viewMode === 'form' && <th className="px-2 py-2.5 text-left font-semibold text-gray-500 min-w-[80px]">Class</th>}
                <th className="px-2 py-2.5 text-center font-semibold text-gray-500 w-10" title="Rank by average — tied students share a rank">Rank</th>
                {subjectList.map((s) => (
                  <th key={s.id} className="px-2 py-2.5 text-center font-semibold text-gray-500 min-w-[60px]">
                    <span className="block truncate max-w-[80px]" title={s.name}>{subjectShort(s)}</span>
                  </th>
                ))}
                <th className="px-3 py-2.5 text-center font-bold text-primary min-w-[50px]">AVG</th>
                <th className="px-2 py-2.5 text-center font-semibold text-gray-500 w-8">P</th>
                <th className="px-2 py-2.5 text-center font-semibold text-gray-500 w-8">F</th>
              </tr>
            </thead>
            <tbody>
              {matrixRows.map((r, i) => (
                <tr key={r.studentId} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/50">
                  <td className="sticky left-0 bg-white dark:bg-gray-900 z-10 px-3 py-2 text-gray-400 font-medium">{i + 1}</td>
                  <td className="sticky left-8 bg-white dark:bg-gray-900 z-10 px-3 py-2 font-medium text-gray-800 dark:text-white truncate max-w-[170px]">{r.studentName}</td>
                  {viewMode === 'form' && <td className="px-2 py-2 text-gray-500 text-xs">{r.className}</td>}
                  <td className="px-2 py-2 text-center text-gray-400">{r.rank || '—'}</td>
                  {subjectList.map((s) => {
                    const m = r.marks[s.id];
                    return (
                      <td key={s.id} className={`px-2 py-2 text-center ${m?.termMark != null ? markColor(m.termMark) : ''}`}>
                        {m?.termMark != null ? <span className="font-medium">{m.termMark}</span> : <span className="text-gray-300">—</span>}
                      </td>
                    );
                  })}
                  <td className={`px-3 py-2 text-center font-bold ${r.average !== null ? markColor(r.average) : ''}`}>{r.average ?? '—'}</td>
                  <td className="px-2 py-2 text-center text-emerald-600 font-medium">{r.passCount || '—'}</td>
                  <td className="px-2 py-2 text-center text-red-500 font-medium">{r.failCount || '—'}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600 font-bold">
                <td className="sticky left-0 bg-gray-50 dark:bg-gray-800 z-10 px-3 py-2.5"></td>
                <td className="sticky left-8 bg-gray-50 dark:bg-gray-800 z-10 px-3 py-2.5 text-gray-600">AVERAGE <span className="font-normal text-gray-400">({matrixRows.length} students)</span></td>
                {viewMode === 'form' && <td></td>}
                <td></td>
                {subjectList.map((s) => <td key={s.id} className="px-2 py-2.5 text-center text-gray-700 dark:text-gray-300">{subjAvg[s.id] ?? '—'}</td>)}
                <td className="px-3 py-2.5 text-center text-primary">{clsAvg ?? '—'}</td>
                <td colSpan={2}></td>
              </tr>
              <tr className="bg-gray-50/60 dark:bg-gray-800/60 border-t border-gray-200 dark:border-gray-700 font-semibold text-gray-500">
                <td className="sticky left-0 bg-gray-50 dark:bg-gray-800 z-10 px-3 py-2"></td>
                <td className="sticky left-8 bg-gray-50 dark:bg-gray-800 z-10 px-3 py-2">MEDIAN</td>
                {viewMode === 'form' && <td></td>}
                <td></td>
                {subjectList.map((s) => <td key={s.id} className="px-2 py-2 text-center text-gray-500 dark:text-gray-400">{subjMed[s.id] ?? '—'}</td>)}
                <td className="px-3 py-2 text-center text-primary/70">{clsMed ?? '—'}</td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </SoftCard>
    );
  }

  function renderMarksheet() {
    if (rows.length === 0) return <SoftCard><p className="text-sm text-gray-500 text-center py-4">No marks found.</p></SoftCard>;
    return renderMatrix(shownSubjects, displayRows, subjectAverages, classAverage, subjectMedians, classMedian);
  }

  function renderBySubject() {
    return (
      <div className="space-y-4">
        <SoftCard pad="p-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
              <BookOpen size={14} /> Department
            </span>
            {availableDepartments.length > 1 ? (
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className={glassFieldClass('sm:w-64')}
              >
                {availableDepartments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            ) : (
              <span className="text-sm font-semibold text-gray-800 dark:text-white">{departmentFilter || '—'}</span>
            )}
            <span className="text-xs text-gray-400 sm:ml-auto">
              {departmentSubjects.length} subject{departmentSubjects.length === 1 ? '' : 's'} · how students are doing in this department
            </span>
          </div>
        </SoftCard>

        {departmentSubjects.length === 0 ? (
          <SoftCard><p className="text-sm text-gray-500 text-center py-6">No {departmentFilter} subjects are offered to this {viewMode === 'class' ? 'class' : 'form'}.</p></SoftCard>
        ) : deptRows.filter((r) => r.average !== null).length === 0 ? (
          <SoftCard><p className="text-sm text-gray-500 text-center py-6">No marks captured yet for {departmentFilter} subjects.</p></SoftCard>
        ) : (
          <>
            {renderMatrix(shownDeptSubjects, displayDeptRows, deptSubjectAverages, deptClassAverage, deptSubjectMedians, deptClassMedian)}
            <SoftCard>
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-4">{departmentFilter} — Subject pass rates</h3>
              <div className="space-y-3">
                {shownDeptSubjects.map((sub) => {
                  const marks = deptRows.map((r) => r.marks[sub.id]?.termMark).filter((m): m is number => m !== null && m !== undefined);
                  const passRate = marks.length > 0 ? Math.round((marks.filter((m) => m >= passMark).length / marks.length) * 100) : null;
                  return (
                    <div key={sub.id} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-32 truncate shrink-0" title={sub.name}>{sub.name}</span>
                      <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden relative">
                        {passRate !== null && (
                          <div className={`h-full rounded-full transition-all ${passRate >= 80 ? 'bg-emerald-500' : passRate >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${passRate}%` }} />
                        )}
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-700 dark:text-gray-200">
                          {passRate !== null ? `${passRate}% pass` : 'No data'}
                        </span>
                      </div>
                      <span className="text-right shrink-0 w-12 text-sm font-bold text-gray-800 dark:text-white">{deptSubjectAverages[sub.id] ?? '—'}</span>
                    </div>
                  );
                })}
              </div>
            </SoftCard>
          </>
        )}
      </div>
    );
  }

  function renderAtRisk() {
    if (atRiskStudents.length === 0) {
      return <SoftCard><p className="text-sm text-center py-6 text-emerald-600 font-medium">No at-risk students detected. All students are meeting the minimum requirements.</p></SoftCard>;
    }
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <SoftCard pad="p-3"><p className="text-xl font-bold text-red-600">{criticalCount}</p><p className="text-xs text-gray-400">Critical</p></SoftCard>
          <SoftCard pad="p-3"><p className="text-xl font-bold text-amber-600">{borderlineCount}</p><p className="text-xs text-gray-400">Borderline</p></SoftCard>
          <SoftCard pad="p-3"><p className="text-xl font-bold text-gray-800 dark:text-white">{atRiskStudents.length}</p><p className="text-xs text-gray-400">Total At-Risk</p></SoftCard>
        </div>
        <SoftCard pad="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-left text-gray-500">
                  <th className="px-4 py-2.5 font-medium">Student</th>
                  <th className="px-4 py-2.5 font-medium">Tier</th>
                  <th className="px-4 py-2.5 font-medium">Average</th>
                  <th className="px-4 py-2.5 font-medium">Failed Subjects</th>
                  <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Reason</th>
                </tr>
              </thead>
              <tbody>
                {atRiskStudents.map((s) => (
                  <tr key={s.studentId} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/50">
                    <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-white">{s.fullName}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        s.tier === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      }`}>
                        {s.tier === 'critical' ? <TrendingDown size={12} /> : <AlertTriangle size={12} />}
                        {s.tier}
                      </span>
                    </td>
                    <td className={`px-4 py-2.5 font-medium ${s.average !== null && s.average < passMark ? 'text-red-600' : 'text-gray-800 dark:text-white'}`}>
                      {s.average !== null ? s.average : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">
                      {s.failedSubjects.map((f) => f.subjectName).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 hidden sm:table-cell">
                      {describeAtRiskReason(s, passMark)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SoftCard>
      </div>
    );
  }

  function renderSummary() {
    // Canonical datasets (lifted to memos) — same numbers the exports use.
    const subjectStats = summaryStats;

    return (
      <div className="space-y-4">
        <SoftCard>
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-4">Subject Performance</h3>
          <div className="space-y-3">
            {subjectStats.map((s) => (
              <div key={s.subject.id} className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-32 truncate shrink-0" title={s.subject.name}>
                  {s.subject.name}
                </span>
                <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden relative">
                  {s.passRate !== null && (
                    <div
                      className={`h-full rounded-full transition-all ${s.passRate >= 80 ? 'bg-emerald-500' : s.passRate >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                      style={{ width: `${s.passRate}%` }}
                    />
                  )}
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-700 dark:text-gray-200">
                    {s.passRate !== null ? `${s.passRate}% pass` : 'No data'}
                  </span>
                </div>
                <div className="text-right shrink-0 w-24">
                  <p className="text-sm font-bold text-gray-800 dark:text-white">{s.avg ?? '—'} <span className="text-[10px] font-normal text-gray-400">avg</span></p>
                  <p className="text-[10px] text-gray-400">med {s.median ?? '—'} · {s.highest ?? '—'}↑ {s.lowest ?? '—'}↓</p>
                </div>
              </div>
            ))}
          </div>
        </SoftCard>

        {/* Grade distribution */}
        <SoftCard>
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-4">Student Average Distribution</h3>
          {(() => {
            const maxCount = Math.max(...avgDistribution.map((b) => b.count), 1);
            return (
              <div className="space-y-2">
                {avgDistribution.map((b) => (
                  <div key={b.label} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-14 shrink-0">{b.label}</span>
                    <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                      <div className={`h-full ${b.color} rounded transition-all`} style={{ width: `${(b.count / maxCount) * 100}%` }} />
                    </div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-8 text-right">{b.count}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </SoftCard>
      </div>
    );
  }
}
